class CompareBar extends HTMLElement {
  connectedCallback() {
    // Set max items based on screen width
    this.maxItems = window.innerWidth < 750 ? 3 : 4;

    this.collectionHandle = this.dataset.collectionHandle || 'default';
    this.dataCache = {};

    this.itemsContainer = this.querySelector('.compare-bar__items');
    this.itemElements = Array.from(
      this.itemsContainer?.querySelectorAll('.compare-bar__item') || []
    );

    // Get status and clear button elements
    this.statusEl = this.querySelector('.compare-bar__status');
    this.clearBtn = this.querySelector('.compare-bar__clear');

    // Listen for clear button click
    this.clearBtn?.addEventListener('click', this.clear.bind(this));
    // Listen for status click to open compare drawer
    this.statusEl?.addEventListener('click', () => {
      const drawer = document.getElementById('CompareDrawer');
      const container = drawer?.querySelector('compare-container');
      container?.update();
      drawer?.open();
    });

    // Listen for custom compare-updated event
    document.addEventListener('compare-updated', (e) => {
      if (e.detail.handle === this.collectionHandle) this.update(e.detail.items);
    });
    // Listen for storage changes (e.g., in other tabs)
    window.addEventListener('storage', (e) => {
      if (e.key === 'compareItems') this.update();
    });

    // Initialize current items and update UI
    this.currentItems = [];
    this.update();

    // Track footer visibility for bar visibility
    this.footerVisible = false;
    this.observeSections();
  }

  // Get compare items from sessionStorage
  getAll() {
    try {
      const data = JSON.parse(sessionStorage.getItem('compareItems'));
      if (!data) return {};
      if (Array.isArray(data)) {
        return { default: data };
      }
      return data;
    } catch (err) {
      return {};
    }
  }

  getItems() {
    const all = this.getAll();
    return all[this.collectionHandle] || [];
  }

  // Save compare items to sessionStorage
  saveItems(items) {
    const all = this.getAll();
    all[this.collectionHandle] = items;
    sessionStorage.setItem('compareItems', JSON.stringify(all));
  }

  // Fetch product data for a given item
  async fetchData(item) {
    if (this.dataCache[item.id]) return this.dataCache[item.id];
    try {
      const url = new URL(item.url, window.location.origin);
      url.searchParams.set('view', 'compare-bar');
      url.searchParams.set('variant', item.id);
      const res = await fetch(url);
      const json = await res.json();
      this.dataCache[item.id] = json;
      return json;
    } catch (err) {
      return null;
    }
  }

  // Update the compare bar UI with current items
  async update(items = this.getItems()) {
    if (!this.itemsContainer) return;

    // Limit entries to maxItems
    const entries = items.slice(0, this.maxItems);

    for (let i = 0; i < this.maxItems; i++) {
      const el = this.itemElements[i];
      const newItem = entries[i];
      const oldItem = this.currentItems[i];

      if (!el) continue;

      if (!newItem) {
        // Show placeholder if no item
        el.innerHTML = '';
        el.classList.add('placeholder');
        delete el.dataset.id;
        continue;
      }

      // If item changed, update content
      if (!oldItem || oldItem.id !== newItem.id) {
        el.innerHTML = '';
        el.classList.remove('placeholder');
        const data = await this.fetchData(newItem);
        if (data && data.image) {
          // Render image and remove button
          el.innerHTML = `<img src="${data.image}" alt="${data.title}"><button type="button" class="compare-bar__remove color-scheme-3" aria-label="Remove"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="icon icon-close"><path d="M6.4 19.2695L5 17.8695L10.6 12.2695L5 6.66953L6.4 5.26953L12 10.8695L17.6 5.26953L19 6.66953L13.4 12.2695L19 17.8695L17.6 19.2695L12 13.6695L6.4 19.2695Z" fill="currentColor" /></svg></button>`;
        } else {
          // Fallback text if no image
          el.textContent = `Variant ${newItem.id}`;
        }
        // Add remove button handler
        el.dataset.id = newItem.id;
        el.querySelector('.compare-bar__remove')?.addEventListener('click', () => this.remove(newItem.id));
      }
    }

    // Update current items
    this.currentItems = entries;

    // Update status text
    if (this.statusEl)
      if (window.innerWidth > 750) {
        this.statusEl.querySelector('.small-hide').textContent = window.compareStrings.barStatus
          .replace('[count]', entries.length)
          .replace('[max]', this.maxItems);
      }

    this.updateVisibility();
  }

  // Remove an item by id
  remove(id) {
    const items = this.getItems().filter((i) => i.id !== id);
    this.saveItems(items);
    this.update(items);
    document.dispatchEvent(
      new CustomEvent('compare-updated', { detail: { items, handle: this.collectionHandle } })
    );
  }

  // Clear all compare items
  clear() {
    const all = this.getAll();
    delete all[this.collectionHandle];
    sessionStorage.setItem('compareItems', JSON.stringify(all));
    const items = [];
    this.update(items);
    document.dispatchEvent(
      new CustomEvent('compare-updated', { detail: { items, handle: this.collectionHandle } })
    );
  }

  // Observe footer to toggle bar visibility
  observeSections() {
    const footer = document.querySelector('footer');

    if (footer) {
      const footerObserver = new IntersectionObserver((entries) => {
        this.footerVisible = entries[0].isIntersecting;
        this.updateVisibility();
      });
      footerObserver.observe(footer);
    }
  }

  // Show/hide bar based on footer visibility
  updateVisibility() {
    const hasAtLeastOne = this.getItems().length > 0;
    if (!this.footerVisible && hasAtLeastOne) {
      this.classList.add('visible');
    } else {
      this.classList.remove('visible');
    }
  }
}

// Register the custom element
customElements.define('compare-bar', CompareBar);
